// @ds/data/auditFields — canonical created_at / updated_at audit fields.
//
// Schemas opt in via `defineSchema<T>()(fields, { audit: true })`. The
// merger inserts both audit entries BEFORE the caller's fields, so the
// caller can override either by re-declaring it with the same key.
//
// The two audit columns appear on nearly every table in the database
// (`created_at TIMESTAMPTZ DEFAULT now()` + `updated_at TIMESTAMPTZ`).
// Declaring them in one place stops the ~8 entity schemas from repeating
// the same {component, data, sortable, readOnly} boilerplate.
//
// Both render via `DateValue` with `variant: "timestamp"` — audit columns
// are conventionally surfaced as full date+time stamps; entity-specific
// formatting overrides at the list-page pickColumns site as needed.
//
// `readOnly: true` because audit columns are written by the database
// (`DEFAULT now()` + UPDATE triggers); the inline-edit affordance must
// never mount for them. `defaultAlign: "right"` because date columns
// conventionally right-align.

import { DateValue, type DateValueProps } from "@ds/display/DateValue";
import type { FieldDef } from "@ds/display/fieldDisplay";

interface AuditableRow {
  created_at?: string | null;
  updated_at?: string | null;
}

export type AuditFieldKey = "created_at" | "updated_at";

/**
 * Pre-built audit-field declarations. Use via `defineSchema<T>()({...},
 * { audit: true })` rather than spreading manually.
 *
 * Each field carries:
 *   - `labelKey`: `common.fields.created_at` / `common.fields.updated_at`
 *   - `component`: DateValue with `variant: "timestamp"` (date + time)
 *   - `deps.fields`: just the timestamp column
 *   - `sortable: true` (capability — both columns are indexed)
 *   - `defaultAlign: "right"` (date columns conventionally right-align)
 *   - `readOnly: true` (DB-managed; inline-edit never mounts)
 */
export const AUDIT_FIELDS = {
  created_at: {
    labelKey: "common.fields.created_at",
    component: DateValue,
    props: { variant: "timestamp" },
    data: (r: AuditableRow) => ({ value: r.created_at ?? null }),
    deps: { fields: ["created_at"] },
    sortable: true,
    defaultAlign: "right",
    readOnly: true,
  } satisfies FieldDef<AuditableRow, DateValueProps>,
  updated_at: {
    labelKey: "common.fields.updated_at",
    component: DateValue,
    props: { variant: "timestamp" },
    data: (r: AuditableRow) => ({ value: r.updated_at ?? null }),
    deps: { fields: ["updated_at"] },
    sortable: true,
    defaultAlign: "right",
    readOnly: true,
  } satisfies FieldDef<AuditableRow, DateValueProps>,
} as const;
